/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
// tslint:disable:no-any
/**
 * State of an experiment
 */

export enum State {
    Draft = 'Draft',
    Active = 'Active',
    Running = 'Running',
    Paused = 'Paused',
    Archived = 'Archived'
}

/**
 * Variations on each experiment
 */
export interface IVariations {
    // friendly name of a variation
    // required
    friendlyName: string;

    // unique id or key for the variation
    // required
    id: string;

    // variation status
    // required. Can be defaulted to active
    status: State;

    // variation weight
    // Optional
    weight?: string;
}

/**
 * Experiments
 */
export interface IExperiments {
    // Friendly name
    // Required
    friendlyName: string;

    // unique id or key of the experiment
    // required
    id: string;

    // variation status
    // required
    status: State;

    // array of variations
    // required
    variations: IVariations[];

    // Created Date
    createdDate?: string;

    // Last modified date
    lastModifiedDate?: string;

    // Last modified by
    lastModifiedBy?: string;

    // Description
    description?: string;

    // Type
    type?: string;

    // Link url
    link?: string;

    resultLink?: string;
}
/**
 * Experiment results interface to display connector name and experiments related to the connector
 */
export interface IExperimentsResult {
    name?: string;
    experiments?: IExperiments[];
}

/**
 * Variants
 */

export interface IVariants {
    // Unique Id or key of the variant
    // Required
    variantId: string;

    // unique id or key of the experiment
    // required
    experimentId: string;
}


export interface ISecretValue {
    value: string;
    id?: string;
    error?: Error;
    expiresOn: number;
}

/**
 * Used to fetch any customer secret values stored in Retail Server Keyvault
 * An initialized class implementing this interface is passed to the initialize method.
 * 
 * Note that the secretManager is only available server-side. Functions that run client side (initializeClientSide, activate)
 * will not be able to use the secretManager
 */
export interface ISecretManager {
    getSecretValue(secretKey: string, baseRetailServerUrl: string): Promise<ISecretValue>;
}

export interface IExperimentationProvider {
    /**
     * Allows the experimentation connector to do any startup related tasks
     * using the config provided by the partner.
     *
     * This method is only called once during server startup.
     * @param config The config provided by the partner in connector settings.
     * @param secretManager Class used to retrieve secrets stored in retail server key vault
     */
    initialize(config: any, secretManager: ISecretManager): Promise<boolean>;

    /**
     * Returns the configuration that should be passed to the experimentation connector
     * when it is initialized client-side
     */
    getConfigForClientSideInit(): Promise<any>;

    /**
     * Initailizes the experimentation provider on the browser (client-side) so that
     * it may activate experiments for a user.
     *
     * @param config The config that is required to initialize the experimentation connector
     * client-side. The result of getConfigForClientSideInit() is passed into this method
     */
    initializeClientSide(config: any): boolean;

    /**
     * Returns a list of all the experiments currently configured whether active or not.
     * This list will be cached and periodically refreshed.
     * @param retailServerBaseUrl The retail server base URL.
     * @param connectorName The optimizely conector name.
     * @param page Optional argument that specifies the page to return.
     * @param items Optional argument that specifies the maximum number of objects to return per request.
     */
    getExperiments(retailServerBaseUrl: string, connectorName: string, page?: string, items?: string): Promise<IExperimentsResult>;

    /**
     * Returns a list of experiments and variants a user will be a part of based
     * off the userId. Optional attributes can provide the connector with additional criteria
     * to determine which experiments a user should be a part of.
     *
     * @param userId userId unique to a user if signed in or unique to a session if user is anonymous.
     * userId will be generated from hash if user is signed-in to deterministically generate sanatized userIds.
     * @param attributes Optional user related attributes
     */
    getVariantsForUser(userId: string, attributes?: { [index: string]: string }): IVariants[];

    /**
     * Activates experiment(s) a user is currently being served. This call will be made
     * client-side after the connector has been initialized client-side
     *
     * @param userId userId unique to a user if signed in or unique to a session if user is anonymous.
     * userId will be generated from hash if user is signed-in to deterministically generate sanatized userIds.
     * @param experiments The experiments the user is participating in.
     * @param attributes Optional user related attributes
     */
    activateExperiment(userId: string, experiments: IVariants[], attributes?: { [index: string]: string }): boolean;
}

export declare enum TelemetryEvent {
    PageView = "PageView",
    ProductPageView = "ProductPageView",
    AddToCart = "AddToCart",
    RemoveFromCart = "RemoveFromCart",
    UpdateInCart = "UpdateInCart",
    CheckOut = "CheckOut",
    Purchase = "Purchase",
    Custom = "Custom"
}

export declare type TelemetryEventContent = IPageViewInfo | ICartInfo | IProductTransaction | ICustomEvent;
/***
 * @property title     {string}   - Page title
 * @property location  {string}   - Page location
 * @property page      {string}   - Page info
 */
export interface IPageViewInfo {
    title: string;
    location?: string;
    page?: string;
}
/***
 * @property productInfo  {IProductInfo}   - Product information
 * @property quantity     {number}         - Quantity of this product involved in this event
 */
export interface ICartInfo {
    products: IProductInfo[];
    orderId: string;
    cartId: string;
    cartVersion: string;
}
/***
 * @property productChannelId       {string}   - Product channel ID
 * @property productChannelName     {string}   - Product channel name
 * @property productCategoryId      {string}   - Product category ID
 * @property productCategoryName    {string}   - Product category name
 * @property productId              {string}   - Product ID
 * @property productName            {string}   - Product name
 * @property productSku             {string}   - Product SKU
 * @property productPrice           {string}   - Product price
 * @property productQuantity        {string}   - Product quantity
 * @property productCurrency        {string}   - Product currency code
 */
export interface IProductInfo {
    productChannelId: string;
    productChannelName: string;
    productCategoryId: string;
    productCategoryName: string;
    productId: string;
    productName: string;
    productSku: string;
    productPrice: string;
    productQuantity: string;
    productCurrency: string;
}
/***
 * @property id            {string}         - Transaction ID
 * @property affiliation   {string}         - Origin of this transaction (e.g. Online Store)
 * @property revenue       {number}         - Revenue from this transaction
 * @property tax           {number}         - Tax amount
 * @property shippingCost  {number}         - Shipping cost
 * @property products      {IProductInfo[]} - List of products in this transaction
 */
export interface IProductTransaction {
    id: string;
    affiliation?: string;
    revenue?: number;
    tax?: number;
    shippingCost?: number;
    products?: IProductInfo[];
}
/***
 * @property contentCategory  {string}   - Custom event category
 * @property contentAction    {object}   - Custom event content
 */
export interface ICustomEvent {
    contentCategory: string;
    contentAction?: {};
}

export interface IExpLogger {
    /**
     * Performs any client-side initialization and setup for the event logger
     * @param config The config that is required to initialize the logger client-side
     * @param userId The current user being tracked
     */
    // tslint:disable-next-line:no-any
    initializeClientSide?(config?: any, userId?: string): void;

    /**
     * Logs the given event and payload
     * @param eventName Name of event to log
     * @param payload Extra parameters dependent on the eventType
     * @param attributes User attributes
     */
    // tslint:disable-next-line:no-any
    logEvent(eventName: string, payload: any, attributes?: any): void;
}

export interface IExperimentationConnector {
    name: string;
    provider: IExperimentationProvider;
    listener?: IExperimentationProvider;
}
